/**
 * Arcana — Currency hover tooltip
 *
 * Site prices stay in NZD. On hover over any price, a small dark
 * tooltip shows the same amount converted to USD, AUD, CNY, and EUR using
 * live ECB rates from frankfurter.app (free, no API key, no signup).
 *
 * Rates are cached in localStorage for 24 hours. If the API is
 * unreachable, falls back to a hardcoded set of recent rates so the
 * tooltip still works (just slightly stale).
 */

(function () {
  'use strict';

  var TARGET_CURRENCIES = ['USD', 'AUD', 'CNY', 'EUR'];
  var SYMBOLS = { USD: 'US$', AUD: 'AU$', CNY: 'CN¥', EUR: '€' };
  var FALLBACK_RATES = { USD: 0.60, AUD: 0.92, CNY: 4.30, EUR: 0.55 };
  var CACHE_KEY = 'arcana_currency_rates_v2';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  var API_URL =
    'https://api.frankfurter.app/latest?from=NZD&to=' + TARGET_CURRENCIES.join(',');

  var ratesPromise = null;
  var tooltipIndex = 0;

  function getCachedRates() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.rates || !entry.timestamp) return null;
      if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
      return entry.rates;
    } catch (e) {
      return null;
    }
  }

  function setCachedRates(rates) {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ timestamp: Date.now(), rates: rates })
      );
    } catch (e) {
      /* ignore quota / private mode */
    }
  }

  function fetchRates() {
    if (ratesPromise) return ratesPromise;
    var cached = getCachedRates();
    if (cached) {
      ratesPromise = Promise.resolve(cached);
      return ratesPromise;
    }
    ratesPromise = fetch(API_URL)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (data && data.rates) {
          setCachedRates(data.rates);
          return data.rates;
        }
        return FALLBACK_RATES;
      })
      .catch(function () { return FALLBACK_RATES; });
    return ratesPromise;
  }

  function formatAmount(value, currency) {
    var symbol = SYMBOLS[currency] || currency + ' ';
    return symbol + value.toFixed(2);
  }

  /**
   * Pull the numeric amount from a price string like "$1,250.00" or
   * "From $45.50 NZD". Returns null if no number is found.
   */
  function parseAmount(text) {
    if (!text) return null;
    var match = text.match(/(\d{1,3}(?:[,\s]\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)/);
    if (!match) return null;
    var n = parseFloat(match[1].replace(/[,\s]/g, ''));
    return isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Find the most relevant price text within a .price element.
   * Prefers sale price if present (it's what the customer pays).
   */
  function readPriceText(priceEl) {
    var sale = priceEl.querySelector('.price-item--sale, .price-item--last');
    if (sale && sale.offsetParent !== null) {
      var saleText = sale.textContent.trim();
      if (saleText) return saleText;
    }
    var regular = priceEl.querySelector(
      '.price-item--regular, .price-item:not(.price-item--last)'
    );
    if (regular) return regular.textContent.trim();
    var any = priceEl.querySelector('.price-item');
    return any ? any.textContent.trim() : priceEl.textContent.trim();
  }

  function buildTooltipHTML(amountNZD, rates) {
    var parts = TARGET_CURRENCIES.map(function (cur) {
      var rate = rates[cur];
      if (!rate) return '';
      return (
        '<span class="arcana-currency-tooltip__row">' +
        formatAmount(amountNZD * rate, cur) +
        '</span>'
      );
    }).filter(Boolean);
    return (
      '<span class="arcana-currency-tooltip__label">Approx.</span>' +
      parts.join('<span class="arcana-currency-tooltip__sep" aria-hidden="true">·</span>')
    );
  }

  function attach(priceEl) {
    if (priceEl.dataset.arcanaCurrencyInit === 'true') return;
    priceEl.dataset.arcanaCurrencyInit = 'true';
    priceEl.classList.add('has-currency-tooltip');

    var tip = null;
    var leaveTimer = null;
    var trigger = priceEl.querySelector('.arcana-currency-indicator');

    function show() {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
      var amount = parseAmount(readPriceText(priceEl));
      if (!amount) return;
      if (!tip) {
        tip = document.createElement('span');
        tip.className = 'arcana-currency-tooltip';
        tip.id = 'ArcanaCurrencyTooltip-' + (++tooltipIndex);
        tip.setAttribute('role', 'tooltip');
        priceEl.appendChild(tip);
        if (trigger) trigger.setAttribute('aria-describedby', tip.id);
      }
      tip.innerHTML = buildTooltipHTML(amount, FALLBACK_RATES);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(function () {
        if (tip) tip.classList.add('is-visible');
      });
      fetchRates().then(function (rates) {
        if (!tip) return;
        tip.innerHTML = buildTooltipHTML(amount, rates);
      });
    }

    function hide() {
      if (!tip) return;
      tip.classList.remove('is-visible');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      leaveTimer = setTimeout(function () {
        if (tip && tip.parentNode) tip.parentNode.removeChild(tip);
        tip = null;
        leaveTimer = null;
      }, 220);
    }

    priceEl.addEventListener('mouseenter', show);
    priceEl.addEventListener('mouseleave', hide);
    priceEl.addEventListener('focusin', show);
    priceEl.addEventListener('focusout', hide);

    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        show();
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          hide();
          trigger.focus();
        }
      });
    }
  }

  function scan(root) {
    if (
      !root ||
      root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11
    ) {
      return;
    }

    if (
      root.nodeType === 1 &&
      root.matches('.price:not([data-arcana-currency-init])')
    ) {
      attach(root);
    }

    var prices = root.querySelectorAll(
      '.price:not([data-arcana-currency-init])'
    );
    for (var i = 0; i < prices.length; i++) attach(prices[i]);
  }

  function init() {
    scan(document);

    // Attach to newly added price nodes only. Rates are requested lazily on
    // the visitor's first hover, focus or click, never merely on page load.
    var cartDrawer = document.querySelector('cart-drawer') ||
      document.querySelector('.cart-drawer');
    var containers = [
      document.getElementById('MainContent'),
      cartDrawer,
      document.getElementById('cart')
    ].filter(function (el, index, items) {
      return el && items.indexOf(el) === index;
    });

    var observers = containers.map(function (el) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          for (var i = 0; i < mutation.addedNodes.length; i++) {
            scan(mutation.addedNodes[i]);
          }
        });
      });
      observer.observe(el, { childList: true, subtree: true });
      return observer;
    });

    function onSectionLoad(event) { scan(event.target); }
    document.addEventListener('shopify:section:load', onSectionLoad);

    // Tear down observers/listeners when the page is unloaded (also covers
    // bfcache) so nothing leaks across navigations.
    function teardown() {
      observers.forEach(function (observer) { observer.disconnect(); });
      observers.length = 0;
      document.removeEventListener('shopify:section:load', onSectionLoad);
      window.removeEventListener('pagehide', teardown);
    }
    window.addEventListener('pagehide', teardown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
